import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import * as S from './WatchCard.style';

interface WatchCardProps {
  watch: {
    id: string;
    brand: string;
    model: string;
    slug: string;
    priceEur: number | string;
    status?: string;
    isAvailable?: boolean;
    isReserved?: boolean;
    isSold?: boolean;
    images?: Array<{
      thumbnailUrl?: string;
      originalUrl?: string;
      altText?: string;
    }>;
  };
}

const WatchCard: React.FC<WatchCardProps> = ({ watch }) => {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();

  // Trust backend computed flags first, fall back to raw status
  const isSold = watch.isSold ?? (watch.status === 'SOLD');
  const isReserved = watch.isReserved ?? (watch.status === 'RESERVED');
  const unavailable = isSold || isReserved;

  const handleClick = () => {
    navigate(`/kaufen/${watch.slug}`);
  };

  const API_URL = process.env.REACT_APP_BACKEND_URL || '';

  const imageUrl = watch.images?.[0]?.thumbnailUrl
    ? `${API_URL}${watch.images[0].thumbnailUrl}`
    : watch.images?.[0]?.originalUrl
    ? `${API_URL}${watch.images[0].originalUrl}`
    : '/placeholder.svg';

  const priceValue = typeof watch.priceEur === 'string' ? parseFloat(watch.priceEur) : watch.priceEur;
  const locale = i18n.language?.startsWith('en') ? 'en-GB' : 'de-DE';
  const formattedPrice = new Intl.NumberFormat(locale, {
    style: 'currency',
    currency: 'EUR',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(priceValue);

  return (
    <S.WatchCardContainer
      onClick={handleClick}
      $unavailable={unavailable}
      data-testid={`watch-card-${watch.slug}`}
      data-status={isSold ? 'sold' : isReserved ? 'reserved' : 'available'}
    >
      <S.ImageWrapper>
        <S.WatchImage
          src={imageUrl}
          alt={watch.images?.[0]?.altText || `${watch.brand} ${watch.model}`}
          $unavailable={unavailable}
          onError={(e) => {
            (e.target as HTMLImageElement).src = '/placeholder.svg';
          }}
        />
        {isSold && (
          <S.StatusBadge $variant="sold" data-testid="watch-card-sold-badge">
            {t('watch_status.sold')}
          </S.StatusBadge>
        )}
        {!isSold && isReserved && (
          <S.StatusBadge $variant="reserved" data-testid="watch-card-reserved-badge">
            {t('watch_status.reserved')}
          </S.StatusBadge>
        )}
      </S.ImageWrapper>
      <S.WatchInfo>
        <S.WatchBrand>{watch.brand}</S.WatchBrand>
        <S.WatchModel>{watch.model}</S.WatchModel>
        <S.WatchPrice>{formattedPrice}</S.WatchPrice>
      </S.WatchInfo>
    </S.WatchCardContainer>
  );
};

export default WatchCard;
